import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "react-oidc-context";

import { AppBar, Toolbar, ListItem, ListItemText, Drawer } from "@mui/material";
import Grid from '@mui/material/Grid2';
import MenuIcon from "@mui/icons-material/Menu"; // "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person"; // '@mui/icons-material/Person';

import { SourceBook } from "@dndModels";
import { DataType, SourceBookType, WorldSetting } from "@dndModels/Types";
import { DATA_TYPE_OPTIONS, FEAT_TIERS, SCHOOLS, SPELL_LEVEL_OPTIONS, WORLD_SETTINGS } from "@dndModels/Constants";
import { DisablableOption } from "@models";

import DandDAPI from "@utilities/smart_api";

import UserMenu from "@components/Navigation/UserMenu";
import Sidebar from "@components/Navigation/Sidebar";
import LoadingBox from "@components/input/LoadingBox";
import SelectSourceBookBox from "@dndComponents/select/SelectSourceBookBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import StringBox from "@components/input/strings/StringBox";
import StringsBox from "@components/input/strings/StringsBox";
import BugButton from "@components/input/buttons/icon_buttons/BugButton";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import FlexBox from "@components/layout/FlexBox";
import ProgressBar from "@components/input/ProgressBar";
import RulesReferenceButton from "@dndComponents/campaign/rules/RulesReferenceButton";

interface Props { 
}

const api = DandDAPI.getInstance();

const SPELL_FIELDS: DisablableOption[] = [
  { id: "school", text: "Magic School" },
  { id: "level", text: "Spell Level"}
];

const FEAT_FIELDS: DisablableOption[] = [
  { id: "feat_tier", text: "Feat Type" },
  // { id: "repeatable", text: "Repeatable"}
];

const DEITY_FIELDS: DisablableOption[] = [
  { id: "pantheons", text: "Pantheon" },
  // { id: "repeatable", text: "Repeatable"}
];

export default function NavBar({
}: Props) {
  // console.log("NavBar");
  const auth = useAuth();
  const [fetched, setFetched] = useState(false);
  const [true_fetched, setTrueFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [finished_loading, setFinishedLoading] = useState(false);
  const [ping, setPing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [source_book_type, setSourceBookType] = useState<SourceBookType | "">(api.working_on_sb !== null ? api.working_on_sb.source_book_type : "");
  const [working_on_sb, setWorkingOnSB] = useState(api.working_on_sb);
  const [reloading, setReloading] = useState(false);
  const [wo_object_open, setWOObjectOpen] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const heightFinder: any = React.createRef();

  const updateHeight = useCallback(() => {
    if (heightFinder.current instanceof HTMLElement) {
      api.topHeight = heightFinder.current.clientHeight;
      // console.log(heightFinder.current.clientHeight);
      setReloading(true);
    }
  }, [heightFinder]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const load = useCallback( async () => {
    if (!true_fetched) {
      setTrueFetched(true);
    }
    if (api.user && api.user.is_admin && !api.working_on_sb) {
      setLoading(true);

      const res = await api.getObjects<SourceBook>({ data_type: "source_book" });
      if (res) {
        // const changed = res.find(o => o.name.includes("Monsters of the"));
        // const changed = res.find(o => o.name.includes("Horrors Within"));
        // const changed = res.find(o => o.name.includes("Pointy Hat"));
        // const changed = res.find(o => o.name.includes("Wild Hearth"));
        const changed = res.find(o => o.name === "Mad Homebrews");
        if (changed instanceof SourceBook) {
          api.working_on_sb = changed;
          setWorkingOnSB(changed);
          setReloading(true);
        }
      }
      setLoading(false);
    } else {
      setReloading(true);
    }
  }, [ping, true_fetched]);

  const reload = useCallback(() => {
    // console.log("Reload");
    // if (true_fetched) { 
      // It's showing as false even though I know it's actually true. I think it's an issue with how I'm using subscriptions. 
      // I've known about this issue for a while, but I need to get it sorted out. 
      // I need to make it properly reload the subscriptions on every render. 
      // Every time a value change it needs to refresh the subscriptions, or any variables used in them will see the old values.
      // I'll take care of that after I finish getting the encounter complications all in.
      setTrueFetched(false);
      setTimeout(() => {
        load();
      }, 100);
    // }
  }, [true_fetched]);

  const authenticated = useCallback(() => {
    // console.log("Authenticated");
    // debugger;
    if (!fetched) {
      setFetched(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched]);

  const postReload = useCallback(() => {
    // console.log("Post Reload");
    setTimeout(() => {
      load();
    }, 100);
  }, [load]);

  useEffect(() => {
    api.subscription_holder.subscribe("Reload", mounted, reload);
    api.subscription_holder.subscribe("Post Reload", mounted, postReload);
    api.subscription_holder.subscribe("Authenticated", mounted, authenticated);
  }, []);
  
  useEffect(() => {
    if (!loading && !finished_loading) {
      setFinishedLoading(true);
      updateHeight();
    }
    // Update height on window resize
    window.addEventListener('resize', updateHeight);

    // Clean up the event listener
    return () => window.removeEventListener('resize', updateHeight);
  }, [loading, finished_loading, updateHeight]);

  const renderWorkingOn = () => {
    // console.log(true_fetched);
    if (true_fetched) {
      // debugger;
      if (api.online) {
        if (api.user) {
          if (api.user.admin) {
            return (
              <FlexBox direction="row">
                <FlexBox>
                  <SelectSourceBookBox
                    name="Working On"
                    type={ source_book_type === "" ? (working_on_sb !== null ? working_on_sb.source_book_type : "") : source_book_type }
                    id={working_on_sb !== null ? working_on_sb._id : ""}
                    archives={false}
                    onChange={(type, id) => {
                      setSourceBookType(type);
                      if (api.isDataTypeLoaded("source_book")) {
                        const changed = (api.getDataTypeFromHash<SourceBook>("source_book")).find(o => o._id === id);
                        if (changed instanceof SourceBook) {
                          api.working_on_sb = api.working_on_sb !== null && api.working_on_sb._id === changed._id ? null : changed;
                          setWorkingOnSB(api.working_on_sb);
                          setPing(!ping);
                        }
                      }
                    }}
                  />
                </FlexBox>
                <FlexBox className="header centered clickable" onClick={() => { setWOObjectOpen(true); }}>
                  Object
                </FlexBox>
                <ModalOrDrawer 
                  open={wo_object_open}
                  onClose={() => {setWOObjectOpen(false)}}>
                  { renderWorkingOnObject() }
                </ModalOrDrawer>
              </FlexBox>
            );
          } else {
            // return (
            //   <div style={{ float: "right" }}>
            //     <BugButton />
            //   </div>
            // );
          }
        }
      } else {
        return (
          <span className="header centered">
            Currently Offline. Limited Access.
          </span>
        );
      }
    }
    return null;
  }

  const renderWorkingOnObject = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12} className="header centered">
          Working on Object
        </Grid>
        <Grid size={12}>
          <SelectStringBox
            name="Setting"
            options={WORLD_SETTINGS}
            extra_options={[{ id: "", text: "None" }]}
            value={api.working_on_setting}
            onChange={(changed: WorldSetting | "") => {
              api.working_on_setting = changed;
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid size={12}>
          <StringsBox
            name="Tags"
            values={api.working_on_tags}
            onChange={(changed: string[]) => {
              api.working_on_tags = changed;
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid size={12}>
          <SelectStringBox
            name="Data Type"
            options={DATA_TYPE_OPTIONS}
            value={api.working_on_object.data_type}
            onChange={(changed: DataType) => {
              api.working_on_object = { data_type: changed };
              setPing(!ping);
            }}
          />
        </Grid>
        { renderWorkingOnObjectOfDataType() }
      </Grid>
    );
  }

  const renderWorkingOnObjectOfDataType = () => {
    // console.log(api.working_on_object.data_type);
    switch (api.working_on_object.data_type) {
      case "spell":
        return (
          <>
            { !reloading && Object.keys(api.working_on_object).map((o, key) => {
              if (o !== "data_type") {
                return (
                  <Grid size={12} key={key}>
                    <Grid container spacing={0} direction="row">
                      <Grid size={5}>
                        <SelectStringBox
                          name="Field"
                          value={o}
                          options={SPELL_FIELDS}
                          onChange={(changed: string) => {
                            api.working_on_object[changed] = api.working_on_object[o];
                            delete api.working_on_object[o];
                            setReloading(true);
                          }}
                        />
                      </Grid>
                      <Grid size={5}>
                        { o === "level" ?
                          <SelectStringBox
                            name="Value"
                            value={api.working_on_object[o]}
                            options={SPELL_LEVEL_OPTIONS}
                            onChange={(changed: string) => {
                              api.working_on_object[o] = changed;
                              setPing(!ping);
                            }}
                          />
                        : o === "school" ?
                          <SelectStringBox
                            name="Value"
                            value={api.working_on_object[o]}
                            options={SCHOOLS}
                            onChange={(changed: string) => {
                              api.working_on_object[o] = changed;
                              setPing(!ping);
                            }}
                          />
                        :
                          <StringBox
                            name="Value"
                            value={api.working_on_object[o]}
                            onBlur={(changed: string) => {
                              api.working_on_object[o] = changed;
                              setPing(!ping);
                            }}
                          />
                        }
                      </Grid>
                      <Grid size={2}>
                        <DeleteButton
                          onClick={() => {
                            delete api.working_on_object[o];
                            setReloading(true);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                );
              }
              return null;
            })}
            <Grid size={12}>
              <AddButton
                disabled={ Object.keys(api.working_on_object).includes("New Field") }
                onClick={() => {
                  api.working_on_object["New Field"] = "";
                  setReloading(true);
                }}
              />
            </Grid>
          </>
        );
      case "feat":
        return (
          <>
            { !reloading && Object.keys(api.working_on_object).map((o, key) => {
              if (o !== "data_type") {
                return (
                  <Grid size={12} key={key}>
                    <Grid container spacing={0} direction="row">
                      <Grid size={5}>
                        <SelectStringBox
                          name="Field"
                          value={o}
                          options={FEAT_FIELDS}
                          onChange={(changed: string) => {
                            api.working_on_object[changed] = api.working_on_object[o];
                            delete api.working_on_object[o];
                            setReloading(true);
                          }}
                        />
                      </Grid>
                      <Grid size={5}>
                        { o === "feat_tier" ?
                          <SelectStringBox
                            name="Value"
                            value={api.working_on_object[o]}
                            options={FEAT_TIERS}
                            onChange={(changed: string) => {
                              api.working_on_object[o] = changed;
                              setPing(!ping);
                            }}
                          />
                        // : o === "repeatable" ?
                        //   <CheckBox
                        //     name="Value"
                        //     value={api.working_on_object[o] === "true"}
                        //     onChange={(changed: boolean) => {
                        //       api.working_on_object[o] = changed ? "true" : "false";
                        //       setPing(!ping);
                        //     }}
                        //   />
                        :
                          <StringBox
                            name="Value"
                            value={api.working_on_object[o]}
                            onBlur={(changed: string) => {
                              api.working_on_object[o] = changed;
                              setPing(!ping);
                            }}
                          />
                        }
                      </Grid>
                      <Grid size={2}>
                        <DeleteButton
                          onClick={() => {
                            delete api.working_on_object[o];
                            setReloading(true);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                );
              }
              return null;
            })}
            <Grid size={12}>
              <AddButton
                disabled={ Object.keys(api.working_on_object).includes("New Field") }
                onClick={() => {
                  api.working_on_object["New Field"] = "";
                  setReloading(true);
                }}
              />
            </Grid>
          </>
        );
      case "deity":
        return (
          <>
            { !reloading && Object.keys(api.working_on_object).map((o, key) => {
              if (o !== "data_type") {
                return (
                  <Grid size={12} key={key}>
                    <Grid container spacing={0} direction="row">
                      <Grid size={5}>
                        <SelectStringBox
                          name="Field"
                          value={o}
                          options={DEITY_FIELDS}
                          onChange={(changed: string) => {
                            api.working_on_object[changed] = api.working_on_object[o];
                            delete api.working_on_object[o];
                            setReloading(true);
                          }}
                        />
                      </Grid>
                      <Grid size={5}>
                        { o === "pantheons" ?
                          <StringBox
                            name="Value"
                            value={api.working_on_object[o]}
                            onBlur={(changed: string) => {
                              api.working_on_object[o] = changed;
                              setPing(!ping);
                            }}
                          />
                        // : o === "repeatable" ?
                        //   <CheckBox
                        //     name="Value"
                        //     value={api.working_on_object[o] === "true"}
                        //     onChange={(changed: boolean) => {
                        //       api.working_on_object[o] = changed ? "true" : "false";
                        //       setPing(!ping);
                        //     }}
                        //   />
                        :
                          <StringBox
                            name="Value"
                            value={api.working_on_object[o]}
                            onBlur={(changed: string) => {
                              api.working_on_object[o] = changed;
                              setPing(!ping);
                            }}
                          />
                        }
                      </Grid>
                      <Grid size={2}>
                        <DeleteButton
                          onClick={() => {
                            delete api.working_on_object[o];
                            setReloading(true);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                );
              }
              return null;
            })}
            <Grid size={12}>
              <AddButton
                disabled={ Object.keys(api.working_on_object).includes("New Field") }
                onClick={() => {
                  api.working_on_object["New Field"] = "";
                  setReloading(true);
                }}
              />
            </Grid>
          </>
        );
      default: 
        return (
          <>
            { !reloading && Object.keys(api.working_on_object).map((o, key) => {
              if (o !== "data_type") {
                return (
                  <Grid size={12} key={key}>
                    <Grid container spacing={0} direction="row">
                      <Grid size={5}>
                        <StringBox
                          name="Field"
                          value={o}
                          onBlur={(changed: string) => {
                            api.working_on_object[changed] = api.working_on_object[o];
                            delete api.working_on_object[o];
                            setReloading(true);
                          }}
                        />
                      </Grid>
                      <Grid size={5}>
                        <StringBox
                          name="Value"
                          value={api.working_on_object[o]}
                          onBlur={(changed: string) => {
                            api.working_on_object[o] = changed;
                            setPing(!ping);
                          }}
                        />
                      </Grid>
                      <Grid size={2}>
                        <DeleteButton
                          onClick={() => {
                            delete api.working_on_object[o];
                            setReloading(true);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                );
              }
              return null;
            })}
            <Grid size={12}>
              <AddButton
                disabled={ Object.keys(api.working_on_object).includes("New Field") }
                onClick={() => {
                  api.working_on_object["New Field"] = "";
                  setReloading(true);
                }}
              />
            </Grid>
          </>
        );
    }
  }

  // ── Sign In (redirects to Cognito Hosted UI) ──────────────────────────────
  const handleSignIn = useCallback(() => {
    localStorage.setItem("redirectRememberURL", window.location.href);

    auth.signinRedirect();
  }, [auth]);

  const renderUserMenu = () => {
    if (!fetched) {
      return (
        <ListItem aria-controls="user-menu" aria-haspopup="true">
          <ProgressBar value={0} max={100} message={"Authenticating"} />
        </ListItem>
      );
    } else if (api.user === null) {
      return (
        <ListItem
          aria-controls="user-menu" aria-haspopup="true"
          className="curvedButton"
          style={{ cursor: 'pointer', width: 'auto' }}
          onClick={handleSignIn}>
          <PersonIcon />
          <ListItemText primary=" Login/Register" />
        </ListItem>
      );
    } else {
      return (
        <UserMenu />
      );
    }
  }

  if (loading) {
    return (<div className="centered"><LoadingBox /></div>);
  } else {
    return (
      <AppBar position="static" ref={heightFinder}>
        <FlexBox direction="row" className="padded">
          <FlexBox xs_expand={0}>
            <ListItem
              aria-controls="user-menu" aria-haspopup="true"
              style={{ cursor: 'pointer', width: 'auto' }}
              onClick={() => setMenuOpen(true)}>
              <MenuIcon />
              <ListItemText primary="Menu" />
            </ListItem>
          </FlexBox>
          <FlexBox>{ renderWorkingOn() }</FlexBox>
          <FlexBox xs_expand={0} className="centered"><RulesReferenceButton /></FlexBox>
          <FlexBox xs_expand={0}>{ renderUserMenu() }</FlexBox>
        </FlexBox>
        <Drawer anchor="left"
          open={menuOpen}
          onClose={() => setMenuOpen(false)}>
          <div className="SidebarLabs">
            { menuOpen &&
              <Sidebar logoText="Homebrew Game Labs"
                onNavigate={() => setMenuOpen(false)}
              />
            }
          </div>
        </Drawer>
      </AppBar>
    );
  }
}
